import { TextField } from 'mui-rff'
import { client } from '../../../../client'
import {
  Checkbox,
  DatePicker,
  Select,
  SingleSelect
} from '../../../../react-final-form-wrappers'
import FormRow from '../../FormRow'
import { OnChange } from 'react-final-form-listeners'
import { makeStyles } from '@material-ui/core/styles'
import Field from '../../../../react-final-form-wrappers/Field'
import { useTranslation } from 'react-i18next'
import i18n from '../../../../i18n'
import { useEffect, useState } from 'react'
import { useForm, useField } from 'react-final-form'

const validPostalCode = (value) => {
  if (!value) {
    return false
  }
  const regex = /^[0-9]{4} ?[A-Za-z]{2}$/
  return !!value.match(regex)
}

const validPhoneNumber = (value) => {
  if (!value) {
    return
  }
  const regex = /[0-9]{10}/g
  return value.replace(/[-\s]+/g, '').match(regex) === null
    ? i18n.t('patientDetailsSection.invalidNumber')
    : undefined
}

const useStyles = makeStyles((theme) => ({
  switchLabel: {
    fontSize: 1
  },
  identityCheckbox: {
    display: 'flex',
    alignItems: 'flex-end',
    height: '100%'
  }
}))

const PatientDetailsSection = () => {
  const { t } = useTranslation('patientDetailsSection')
  let changeStreet, changeResidence
  const classes = useStyles()
  const form = useForm()
  const { input: bsnField } = useField('patient.bsn', { subscription: { value: true } })
  const { input: patientIdField } = useField('patient.id', { subscription: { value: true } })
  const [bsnDuplicateError, setBsnDuplicateError] = useState(false)

  const trimmedBsn = bsnField.value?.trim()
  const bsnLengthError =
    trimmedBsn && (trimmedBsn.length < 8 || trimmedBsn.length > 9)
      ? 'Een geldig BSN is 8 of 9 getallen lang.'
      : null

  useEffect(() => {
    let timeout
    const value = bsnField.value?.trim()
    if (!value) {
      setBsnDuplicateError(false)
      return
    }
    if (value.length >= 8 && value.length <= 9) {
      timeout = setTimeout(async () => {
        try {
          const exists = await client.isBsnTaken(value, patientIdField?.value)
          if (exists) {
            setBsnDuplicateError(true)
            form.mutators.setValue('patient.bsnDuplicateError', true)
          } else {
            setBsnDuplicateError(false)
            // unset the flag so we don't pollute autosave payloads
            form.mutators.setValue('patient.bsnDuplicateError', undefined)
          }
        } catch (e) {
          // ignore connectivity errors for this check
        }
      }, 400)
    } else {
      setBsnDuplicateError(false)
    }
    return () => timeout && clearTimeout(timeout)
  }, [bsnField.value, form, patientIdField?.value])

  const bsnErrorMessage = bsnLengthError
    || (bsnDuplicateError ? 'Er bestaat al een dossier met dit bsn-nummer, neem contact op met het secretariaat' : null)

  return (
    <>
      <Field name="patient.street" subscription={{}}>
        {({ input: { onChange } }) => {
          changeStreet = onChange
          return <></>
        }}
      </Field>
      <Field name="patient.residence" subscription={{}}>
        {({ input: { onChange } }) => {
          changeResidence = onChange
          return <></>
        }}
      </Field>

      <FormRow xs={[5, 5, 2]}>
        <Field
          style={{ width: '100%' }}
          name="patient.birthDate"
          component={DatePicker}
          label={t('birthDate')}
        />
        <TextField
          fullWidth
          name="patient.bsn"
          label="BSN"
          error={!!bsnErrorMessage}
          helperText={bsnErrorMessage || undefined}
        />
        <div className={classes.identityCheckbox}>
          <Field
            className={classes.switchLabel}
            type="checkbox"
            name="patient.identityVerified"
            label={t('identityVerify')}
            labelVariant="caption"
            component={Checkbox}
            style={{ marginLeft: 0, marginRight: 0 }}
          />
        </div>
      </FormRow>

      <FormRow xs={[4]}>
        <Select
          style={{ width: 300 }}
          label={t('greeting')}
          getOptionLabel={(i) => i.name}
          getOptionValue={(i) => i.id}
          getOptions={async () => client.getSalutationOptions()}
          name="patient.gender.id"
        />
      </FormRow>

      <FormRow xs={[4, 3, 5]}>
        <TextField fullWidth name="patient.firstName" label={t('firstName')} />
        <TextField fullWidth name="patient.infix" label={t('infix')} />
        <TextField fullWidth name="patient.lastName" label={t('lastName')} />
      </FormRow>

      <FormRow xs={[4, 3, 5]}>
        <TextField fullWidth name="patient.initials" label={t('initials')} />
        <TextField
          fullWidth
          name="patient.partnerInfix"
          label="Tussenvoegsel partner"
        />
        <TextField
          fullWidth
          name="patient.partnerName"
          label={t('partnerName')}
        />
      </FormRow>

      <FormRow>
        <TextField
          fullWidth
          name="patient.postalCode"
          label="Postcode"
          fieldProps={{
            format: (value) =>
              value && value.replace(/[^0-9a-zA-Z ]/gi, '').toUpperCase(),
            parse: (value) => value && value.toUpperCase()
          }}
        />
        <TextField
          fullWidth
          name="patient.houseNumber"
          label={t('houseNumber')}
        />
        <TextField
          fullWidth
          name="patient.houseNumberAddition"
          label={t('addition')}
        />
      </FormRow>

      <FormRow>
        <TextField fullWidth name={'patient.street'} label={t('street')} />
      </FormRow>

      <FormRow>
        <TextField
          fullWidth
          name={'patient.residence'}
          label={t('residence')}
        />
      </FormRow>

      <FormRow>
        <Field
          style={{ width: 500 }}
          label={t('country')}
          getOptionLabel={(i) => i.name}
          getOptionValue={(i) => i.id}
          getOptions={async () => client.getCountryOptions()}
          name="patient.country.id"
          component={SingleSelect}
          defaultLabel="Nederland"
        />
      </FormRow>

      <FormRow>
        <TextField
          fullWidth
          name={'patient.phoneNumber'}
          label={t('phoneNumber')}
          fieldProps={{ validation: validPhoneNumber }}
        />
      </FormRow>

      <FormRow>
        <TextField fullWidth name="patient.email" label="E-mail" />
      </FormRow>

      <OnChange name="patient.postalCode">
        {async (value) => {
          if (validPostalCode(value)) {
            try {
              const { street, residence } = await client.addressLookup(value)
              changeStreet(street)
              changeResidence(residence)
            } catch (e) {
              console.error('failed to lookup address by postal code')
              console.error(e)
            }
          }
        }}
      </OnChange>
    </>
  )
}

export default PatientDetailsSection
